import { ProFormColumnsType } from '@ant-design/pro-components';
import type { ReactNode } from 'react';
import { BaseFormSchemaWithJsxMessagesProps } from '../types';
import { AssertionType } from 'api/btlApplicationForm/types';

type AdditionalInformationSchemaProps = {
  isRefinancing: boolean;
  isExpat: boolean;
  isHmoProperty: boolean;
  showFullApplicationAssertions: boolean;
  /** BTL-only: tax expert advice + non-consumer CBTL confirmation. Default true. */
  includeBtlOnlyAssertions?: boolean;
} & BaseFormSchemaWithJsxMessagesProps;

const DIP_ASSERTION_OPTIONS = (formatMessage: AdditionalInformationSchemaProps['formatMessage']) => [
  {
    label: formatMessage(
      { id: 'btlApplicationForm.ADDITIONAL_INFORMATION.privacyAccepted.label' },
      {
        privacyPolicyLink: (str: string) => (
          <a
            target='_blank'
            href='https://www.strideup.co/privacy-policy'
            rel='noreferrer'
            onClick={e => e.stopPropagation()}
          >
            {str}
          </a>
        ),
      },
    ),
    value: AssertionType.PRIVACY,
  },
  {
    label: formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.creditSearch.label' }),
    value: AssertionType.CREDIT_CONSENT,
  },
];

const FULL_APPLICATION_ASSERTION_OPTIONS = (props: AdditionalInformationSchemaProps) =>
  [
    ...DIP_ASSERTION_OPTIONS(props.formatMessage),
    {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.declarationCopy.label' }),
      value: AssertionType.DECLARATION_COPY,
    },
    props.includeBtlOnlyAssertions !== false && {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.taxExpertAdvice.label' }),
      value: AssertionType.TAX_ADVICE,
    },
    props.includeBtlOnlyAssertions !== false && {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.isConsumer.label' }),
      value: AssertionType.NON_CONSUMER,
    },
    props.isHmoProperty && {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.letToFamily.label' }),
      value: AssertionType.LET_TO_FAMILY,
    },
    props.isRefinancing && {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.refinancingCapitalGains.label' }),
      value: AssertionType.CAPITAL_GAINS_TAX_RISK,
    },
    props.isExpat && {
      label: props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.expatDeclaration.label' }),
      value: AssertionType.EXPAT_NON_RESIDENT,
    },
  ].filter(Boolean) as { label: ReactNode; value: AssertionType }[];

function getAssertionOptions(props: AdditionalInformationSchemaProps) {
  return props.showFullApplicationAssertions
    ? FULL_APPLICATION_ASSERTION_OPTIONS(props)
    : DIP_ASSERTION_OPTIONS(props.formatMessage);
}

function resolveRequiredAssertionValues(props: AdditionalInformationSchemaProps): AssertionType[] {
  return getAssertionOptions(props).map(option => option.value);
}

export const additionalInformationFormSchema = (props: AdditionalInformationSchemaProps): ProFormColumnsType[] => [
  {
    dataIndex: 'checkboxes',
    valueType: 'checkbox',
    className: 'mb-4',
    fieldProps: {
      className: 'flex flex-col gap-3 [&_.ant-checkbox-wrapper]:items-start [&_.ant-checkbox-wrapper]:!ml-0',
      options: getAssertionOptions(props),
    },
    formItemProps: {
      className: '[&_.ant-form-item-control-input]:overflow-visible [&_.ant-form-item-control-input]:mb-2',
      validateTrigger: 'onSubmit',
      rules: [
        {
          validator: (_, value) => {
            const requiredValues = resolveRequiredAssertionValues(props);

            return requiredValues.every(requiredValue => value?.includes(requiredValue))
              ? Promise.resolve()
              : Promise.reject(
                  new Error(
                    props.formatMessage({
                      id: 'btlApplicationForm.SUBMIT_DIP.allTermsRequired.label',
                    }),
                  ),
                );
          },
        },
      ],
    },
  },
  // The notes section will only be displayed when the Broker is able to submit a DIP or the application
  // This is because we add this text to the Case Notes in the model and it's difficult to re-hydrate
  // a specific note to display here.
  {
    hideInForm: !props.isFormEditable || !props.showFullApplicationAssertions,
    renderFormItem: () => (
      <div className={'-mb-6'}>
        <h2 className={'-mb-4'}>
          {props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.notes.header' })}
        </h2>
        <p>{props.formatMessage({ id: 'btlApplicationForm.ADDITIONAL_INFORMATION.notes.message' })}</p>
      </div>
    ),
  },
  {
    hideInForm: !props.isFormEditable || !props.showFullApplicationAssertions,
    dataIndex: 'notes',
    valueType: 'textarea',
    fieldProps: {
      autoSize: {
        minRows: 6,
        maxRows: 30,
      },
    },
  },
];
